import { type ReactNode, useEffect, useRef } from 'react'

type Props = {
  /** Чем окно подписано — им же его называет читалка. */
  title: string
  onClose: () => void
  children: ReactNode
}

/**
 * Модальное окно поверх экрана.
 *
 * Нативным <dialog>, а не своим слоем на div'ах, и это не экономия кода.
 * Платформа даёт то, что руками делается плохо: ловушку фокуса (Tab не уходит
 * за окно), Escape, недоступность того, что под окном, для читалки и мыши, и
 * верхний слой — окно не обрезается ничьим overflow и ни с кем не спорит
 * z-index'ом. Спорить было бы с чем: у шапки партии 7, у меню шапки 8,
 * у приближённой доски свой слой.
 *
 * Окно живёт по адресу (/rules, /settings), а не флагом в состоянии: ссылкой
 * на правила делятся, а «назад» браузера обязана закрывать окно, а не уводить
 * с экрана. Поэтому закрытие здесь только СООБЩАЕТСЯ наружу — адрес меняет
 * тот, кто окном владеет.
 */
export function Modal({ title, onClose, children }: Props) {
  const box = useRef<HTMLDialogElement>(null)
  const inside = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const dialog = box.current

    if (dialog === null) return

    /*
     * Проверка на функцию — ради jsdom: в нём <dialog> объявлен, а showModal
     * не реализован (проверено на jsdom 30). Без неё падали бы все тесты,
     * которые рендерят приложение. Открытое атрибутом окно ведёт себя как
     * обычный слой — без ловушки фокуса и верхнего слоя, — и это ровно та
     * разница, которой в тестах не проверяют.
     */
    if (typeof dialog.showModal === 'function') dialog.showModal()
    else dialog.setAttribute('open', '')

    /*
     * Фокус — на содержимое, а не на крестик.
     *
     * showModal сам ставит фокус на первый элемент, который его принимает, а
     * первым в разметке лежит крестик: окно открывалось с обведённой кнопкой
     * «закрыть», будто игра предлагает её нажать. Содержимое для этого и
     * помечено tabindex="-1" — оно фокус принимает, но из обхода по Tab
     * выпадает. Заодно длинные правила сразу листаются стрелками.
     */
    inside.current?.focus()

    return () => {
      if (typeof dialog.close === 'function') dialog.close()
    }
  }, [])

  return (
    // Клавиатурная пара нажатию мимо окна — Escape, и её принимает onCancel
    // ниже. Правило требует обработчик клавиш на том же элементе и такой
    // развилки не знает.
    // biome-ignore lint/a11y/useKeyWithClickEvents: закрытие с клавиатуры — onCancel
    <dialog
      className="sheet"
      ref={box}
      aria-label={title}
      /*
       * cancel — это Escape и системный жест закрытия. preventDefault
       * обязателен: без него окно закроется само, а адрес останется прежним,
       * и следующий рендер откроет его заново.
       */
      onCancel={(event) => {
        event.preventDefault()
        onClose()
      }}
      /*
       * Нажатие мимо окна. У <dialog> подложка — часть самого элемента,
       * поэтому «мимо» — это попадание ровно в него, а не в панель внутри.
       */
      onClick={(event) => {
        if (event.target === box.current) onClose()
      }}
    >
      <div className="sheet__panel">
        {/*
          Крестик — в углу панели, а прокручивается содержимое ВНУТРИ неё.
          
          Двумя слоями, а не одним, ровно затем, чтобы крестик никуда не
          девался и при этом ничего не заслонял. Что не подошло: плавающий
          крестик ужимал содержимое (grid-контейнеры не подтекают под float),
          а липкая полоса поверх прокрутки оказывалась широкой плашкой,
          закрывающей текст, — и всё равно с щелью над собой, потому что
          `sticky: top 0` прижимается не к краю панели, а к краю области
          прокрутки, то есть ниже её поля.
        */}
        <button className="sheet__close" type="button" onClick={onClose} aria-label="закрыть">
          ×
        </button>
        <div className="sheet__scroll" ref={inside} tabIndex={-1}>
          {children}
        </div>
      </div>
    </dialog>
  )
}
